import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createFilesSchemaYup } from 'utils/validate';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { FileFormProps } from './index';
import { TFilePhoto, User } from 'types/user';
import { StorageServices } from 'services/storage';
import { ROLE } from 'const/common';
import { optionArrearsOther } from 'const/option';
import { adminGetAllUser } from 'services/user';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { OptionAutocomplete } from '../../../../../components/elements/MultiSelectAutocomplete';
import { isNull, omitBy } from 'lodash';
import { CreateConditionReportDto } from '../../../../FileDetail/components/ModalReport';
import { createReport } from 'services/communication';
import dayjs from 'dayjs';
import { requestedMvs, updateFilesBailiff } from 'services/files';
import { useFlash } from 'contexts/flash';
import { useNavigate } from 'react-router-dom';
import { leadTypeOrder } from '../../../../FileDetail/DetailMobile';
import axios from 'axios';
import { CreateLeadPayload, updateLead } from 'services/leads';
import { createFileNote, CreateFileNotePayload } from 'services/fileNote';

export type CreateFiles = {
  fileNumber: string;
  firstName: string;
  lastName: string;
  coBorrowerFirstName?: string;
  coBorrowerLastName?: string;
  vin: string;
  model: string;
  make: string;
  year: string;
  colour?: string;
  plate?: string;
  onHold?: boolean;
  quickNote?: string;
  fileDocuments?: TFilePhoto[];
  photos?: TFilePhoto[];
  arrears: number;
  arrearsOther?: string;
  contractBalance: number;
  ppsaBaseRegistrationNumber: string;
  ppsaRegistrationDate: Date;
  contactDate: Date;
  auctionDeliveryLocationId: number;
  clientOptionsId: number;
  clientOptionsChildId?: number | null;
  userAssign?: OptionAutocomplete[];
};

export type ClientOption = {
  id: number;
  value: string;
  parentId?: number;
  children?: ClientOption[];
};

const storage = new StorageServices();

export const useInjection = ({ createForm, initFile, leads, reloadPage }: FileFormProps) => {
  const { setFlash } = useFlash();
  const navigate = useNavigate();
  const methods = useForm<CreateFiles>({
    resolver: yupResolver(createFilesSchemaYup),
    defaultValues: { ...omitBy(initFile, isNull), arrearsOther: optionArrearsOther[0].value }
  });
  const [optionsUser, setOptionsUser] = useState<OptionAutocomplete[]>([]);
  const [idsLeadOpen, setIdsLeadOpen] = useState<number[]>([]);
  const [modalNewLead, setModalNewLead] = useState<boolean>(false);
  const [openForm, setOpenForm] = useState<boolean>(false);

  useEffect(() => {
    methods.reset({
      ...omitBy(initFile, isNull),
      arrearsOther: initFile?.arrearsOther || optionArrearsOther[0].value
    });
  }, [initFile]);

  const getUserBailiff = useCallback(async () => {
    if (storage.getRole() === 'USER') return;
    const data = await adminGetAllUser({ isActive: true });
    const dataUserConvert = (data.users as User[]).map((item) => ({
      label: `${item.firstName} ${item.lastName} ( ${item.email} )`,
      value: item.id
    }));
    setOptionsUser(dataUserConvert);
  }, [storage]);
  const { asyncCallback: asyncFetchBailiff } = useAsyncCallback(getUserBailiff, []);

  useEffect(() => {
    asyncFetchBailiff();
  }, []);

  const isAdmin = useMemo(() => {
    const role = storage.getRole();
    return role === ROLE.ADMIN || role === ROLE.ROOT_ADMIN;
  }, [storage.getRole()]);

  useEffect(() => {
    if (initFile?.id && !initFile?.leads.length && isAdmin) {
      setModalNewLead(true);
    }
  }, [initFile, isAdmin]);

  const isCompleted = useMemo(() => {
    return initFile?.status === 'COMPLETED';
  }, [initFile?.status]);

  const onSubmitForm = useCallback(
    (value: CreateFiles) => {
      createForm({ value });
    },
    [createForm]
  );

  const sortedLeads = leads?.slice().sort((a, b) => {
    const indexA = leadTypeOrder.indexOf(a.type ?? '');
    const indexB = leadTypeOrder.indexOf(b.type ?? '');
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
  const [showModalReport, setShowModalReport] = useState<boolean>(false);

  const handleCreateReport = useCallback(
    async (value: CreateConditionReportDto) => {
      await createReport({
        ...value,
        inspectionDate: dayjs(value.inspectionDate).hour(12).minute(0).second(0).toISOString()
      });
      setFlash({ type: 'success', message: 'Created successful' });
      reloadPage && (await reloadPage());
      setShowModalReport(false);
    },
    [reloadPage, setFlash]
  );
  const { asyncCallback: asyncHandleCreateReport } = useAsyncCallback(handleCreateReport, []);

  const handleOnRedemption = useCallback(async () => {
    if (!window.confirm('Are you sure you want to redemption file')) return;
    const fileNote: CreateFileNotePayload = {
      fileId: Number(initFile?.id),
      content: 'Moved to Pre-Seizure'
    };
    await Promise.all([
      updateFilesBailiff(Number(initFile?.id), 'PRE_SEIZURE'),
      createFileNote(fileNote)
    ]);
    reloadPage && (await reloadPage());
    setFlash({ type: 'success', message: 'Update files successful' });
  }, [initFile?.id, reloadPage, setFlash]);

  const { asyncCallback: handleUpdateBailiff } = useAsyncCallback(handleOnRedemption, []);

  const handleUpdateLead = useCallback(
    async ({
      idLead,
      payload,
      message
    }: {
      idLead?: number;
      payload: Partial<CreateLeadPayload>;
      message: string;
    }) => {
      if (!idLead || !initFile?.id) return;
      const data = await updateLead(idLead, { ...payload, fileId: Number(initFile?.id) });
      reloadPage && (await reloadPage());

      setFlash({ type: 'success', message });
      if (data?.onHold) {
        navigate(-1);
      }
    },
    [initFile?.id, reloadPage, setFlash, navigate]
  );

  const { asyncCallback: asyncHandleUpdateLead } = useAsyncCallback(handleUpdateLead, []);

  const handleAttendance = useCallback(
    async ({
      id,
      files,
      textNote
    }: {
      id?: number;
      files?: File[];
      textNote: string;
      isEarly?: boolean;
    }) => {
      const formData = new FormData();
      let attendanceIds: number[] = [];
      if (files?.length) {
        for (let x = 0; x < files.length; x++) {
          formData.append('files', files[x], files[x].name);
        }
        const { data: resListIdFile } = await axios.post('/storages/note-photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        attendanceIds = resListIdFile.files;
      }
      const payload = {
        notes: [
          {
            content: textNote,
            ...(attendanceIds.length > 0 ? { notePhotosIds: attendanceIds } : {})
          }
        ]
      };
      await asyncHandleUpdateLead({
        idLead: id,
        payload,
        message: 'Update leads successful'
      });
    },
    [asyncHandleUpdateLead]
  );

  const { asyncCallback: handleAddNotePhotos } = useAsyncCallback(handleAttendance, []);

  const handleRequestMvs = useCallback(
    async (fileId: number, mvsRequested: boolean) => {
      await requestedMvs(fileId, mvsRequested);
      reloadPage && (await reloadPage());
    },
    [reloadPage]
  );

  const { asyncCallback: asyncHandleRequestMvs } = useAsyncCallback(handleRequestMvs, []);

  return {
    methods,
    onSubmitForm,
    isAdmin,
    isCompleted,
    idsLeadOpen,
    setIdsLeadOpen,
    optionsUser,
    modalNewLead,
    setModalNewLead,
    openForm,
    sortedLeads,
    setOpenForm,
    showModalReport,
    asyncHandleCreateReport,
    handleChooseNotRegister: async (idLead: number, isRegisterBC: boolean) => {
      await asyncHandleUpdateLead({
        idLead,
        payload: { isRegisterBC },
        message: 'Updated successful'
      });
    },
    handleUpdateBailiff: handleUpdateBailiff,
    setShowModalReport,
    handleAddNotePhotos: handleAddNotePhotos,
    handleChooseEarlyMorning: async (idLead: number, earlyMorning: boolean) =>
      await asyncHandleUpdateLead({
        idLead,
        payload: { earlyMorning },
        message: 'Update early morning successful'
      }),
    handleChooseDoorKnock: async (idLead: number, doorKnock: boolean) =>
      await asyncHandleUpdateLead({
        idLead,
        payload: { doorKnock },
        message: 'Updated successful'
      }),
    asyncHandleRequestMvs,
    onClickSeized: useCallback(() => {
      isAdmin ? navigate(`/admin/seizure/${initFile?.id}`) : navigate(`/seizure/${initFile?.id}`);
    }, [initFile?.id, isAdmin, navigate])
  };
};
